
import { useState } from 'react'
import { 
  generateVideoWithTTS, 
  convertImageToMp4, 
  generateTTS,
  generateProjectTTS,
  generateAllProjectTTS,
  generateProjectSubtitle,
  convertProjectVideo,
  completeProjectWorkflow,
  concatenateVideos,
  concatenateProjectVideos, 
  addSubtitleToVideo, 
  API_BASE 
} from '../services/api'
import logger from '../utils/logger'

/**
 * 비디오 생성 관련 로직을 위한 커스텀 훅
 */
function useVideoGeneration(projectId = null) {
  const [isGeneratingTTS, setIsGeneratingTTS] = useState({})
  const [isConvertingMp4, setIsConvertingMp4] = useState({})
  const [isGeneratingAllTTS, setIsGeneratingAllTTS] = useState(false)
  const [isGeneratingAllVideos, setIsGeneratingAllVideos] = useState(false)
  const [isMergingVideos, setIsMergingVideos] = useState(false)
  const [mergeProgress, setMergeProgress] = useState(0)
  const [mergeStatus, setMergeStatus] = useState('')

  // TTS 생성 (백엔드 API 우선, 폴백 지원)
  const handleGenerateTTS = async (sectionId, text, voiceType, updateSectionFiles, getSectionById) => {
    setIsGeneratingTTS(prev => ({ ...prev, [sectionId]: true }))
    
    try {
      let audioUrl
      
      // 프로젝트 ID가 있으면 백엔드 API 사용
      if (projectId && projectId !== 'new') {
        logger.debug('🎯 백엔드 프로젝트 TTS API 사용')
        const currentSection = getSectionById(sectionId)
        audioUrl = await generateProjectTTS(
          projectId, 
          sectionId, 
          text, 
          { voice: voiceType, speed: 1.0 }
        )
      } else {
        // 프로젝트 ID가 없으면 기존 API 사용
        logger.debug('📻 기본 TTS API 사용 (로컬)')
        audioUrl = await generateTTS(text, voiceType)
      }
      
      const currentSection = getSectionById(sectionId)
      
      // TTS 오디오만 저장 (비디오 생성 없음)
      logger.debug(`🎯 Section ${sectionId} - 오디오 URL 저장:`, audioUrl)
      
      // Blob URL 검증
      if (audioUrl && audioUrl.startsWith('blob:')) {
        try {
          const testResponse = await fetch(audioUrl)
          const testBlob = await testResponse.blob()
          logger.debug(`✅ Section ${sectionId} Blob URL 검증 성공 - Size: ${testBlob.size} bytes`)
        } catch (error) {
          logger.error(`❌ Section ${sectionId} Blob URL 검증 실패:`, error)
        }
      }
      
      updateSectionFiles(sectionId, {
        image: currentSection?.imageFile,
        video: currentSection?.videoFile,
        url: currentSection?.urlMedia,
        audio: audioUrl,
        mp4: currentSection?.mp4File,
        hasAudioInVideo: currentSection?.hasAudioInVideo || false
      })

      logger.debug('✅ TTS 음성 생성 완료')

    } catch (error) {
      logger.error('TTS 생성 오류:', error)
      alert('음성 생성 중 오류가 발생했습니다.')
    } finally {
      setIsGeneratingTTS(prev => ({ ...prev, [sectionId]: false }))
    }
  }

  // TTS 포함 비디오 생성 (백엔드 API 우선, 폴백 지원)
  const handleGenerateVideoWithTTS = async (sectionId, imageFile, text, voiceType, subtitleSettings, updateSectionFiles, getSectionById = null, videoSettings = null, titleSettings = null) => {
    setIsConvertingMp4(prev => ({ ...prev, [sectionId]: true }))
    
    try {
      logger.debug('🎬 TTS 포함 비디오 생성 시작:', imageFile.name, text)
      logger.debug('📺 비디오 설정:', videoSettings)
      logger.debug('🎛️ 자막 활성화 상태:', subtitleSettings?.enabled !== false)
      
      let mp4Url
      
      // 프로젝트 ID가 있으면 백엔드 API 사용
      if (projectId && projectId !== 'new' && imageFile) {
        logger.debug('🎯 백엔드 비디오 변환 API 사용')
        
        // 자막 설정에 활성화 상태 포함
        const enhancedSubtitleSettings = {
          ...subtitleSettings,
          enabled: subtitleSettings?.enabled !== false
        }
        
        try {
          mp4Url = await convertProjectVideo(
            projectId,
            sectionId,
            imageFile,
            text,
            voiceType,
            enhancedSubtitleSettings,
            videoSettings,
            titleSettings
          )
          logger.debug('✅ 백엔드 비디오 변환 성공:', mp4Url)
        } catch (backendError) {
          logger.error('백엔드 비디오 변환 실패, 로컬 처리로 폴백:', backendError)
          // 폴백: 기존 로컬 API 사용
          mp4Url = await generateVideoWithTTS(imageFile, text, voiceType, enhancedSubtitleSettings, null, titleSettings)
        }
      } else {
        // 프로젝트 ID가 없으면 기존 로컬 API 사용
        logger.debug('📻 기본 비디오 변환 API 사용 (로컬)')
        const enhancedSubtitleSettings = {
          ...subtitleSettings,
          enabled: subtitleSettings?.enabled !== false
        }
        mp4Url = await generateVideoWithTTS(imageFile, text, voiceType, enhancedSubtitleSettings, null, titleSettings)
      }
      
      // 해당 섹션에 TTS 포함 MP4 파일 정보 업데이트
      const currentSection = getSectionById ? getSectionById(sectionId) : null
      updateSectionFiles(sectionId, {
        image: currentSection?.imageFile,
        video: currentSection?.videoFile,
        url: currentSection?.urlMedia,
        audio: currentSection?.audioFile,
        mp4: mp4Url,
        hasAudioInVideo: true
      })

      logger.debug('✅ TTS 포함 비디오 생성 완료')
      return mp4Url

    } catch (error) {
      logger.error('❌ TTS 포함 비디오 생성 오류:', error)
      alert('TTS 포함 비디오 생성 중 오류가 발생했습니다.')
      return null
    } finally {
      setIsConvertingMp4(prev => ({ ...prev, [sectionId]: false }))
    }
  }

  // 기존 비디오에 자막 추가
  const handleAddSubtitleToVideo = async (sectionId, videoFile, subtitleText, subtitleSettings, updateSectionFiles, getSectionById = null, titleSettings = null) => {
    setIsConvertingMp4(prev => ({ ...prev, [sectionId]: true }))
    
    try {
      logger.debug('📝 비디오에 자막 추가 시작:', subtitleText)
      
      // 비디오에 자막 추가
      const mp4Url = await addSubtitleToVideo(
        videoFile,
        subtitleText,
        subtitleSettings,
        titleSettings
      )
      
      logger.debug('✅ 자막 추가 완료:', mp4Url)
      
      // 상태 업데이트
      updateSectionFiles(sectionId, {
        mp4: mp4Url,
        hasAudioInVideo: true
      })
      
      return mp4Url
    } catch (error) {
      logger.error('❌ 자막 추가 실패:', error)
      throw error
    } finally {
      setIsConvertingMp4(prev => ({ ...prev, [sectionId]: false }))
    }
  }

  // 이미지를 MP4로 변환 (백엔드 API 우선, 폴백 지원)
  const handleConvertImageToMp4 = async (sectionId, imageFile, getSectionById, updateSectionFiles, subtitleSettings = null, ttsAudioUrl = null, videoSettings = null, titleSettings = null) => {
    setIsConvertingMp4(prev => ({ ...prev, [sectionId]: true }))
    
    try {
      logger.debug('🎬 이미지를 MP4로 변환 시작:', imageFile.name)
      
      let mp4Url
      
      // 프로젝트 ID가 있고 이미지 파일이 있으면 백엔드 API 사용
      if (projectId && projectId !== 'new' && imageFile) {
        logger.debug('🎯 백엔드 이미지→비디오 변환 API 사용')
        
        const currentSection = getSectionById(sectionId)
        const text = currentSection?.script || ''
        
        try {
          // 백엔드 API 사용 (텍스트가 있으면 TTS도 함께 생성)
          mp4Url = await convertProjectVideo(
            projectId,
            sectionId,
            imageFile,
            text,
            'female',  // 기본 음성
            subtitleSettings,
            videoSettings,
            titleSettings
          )
          logger.debug('✅ 백엔드 이미지 변환 성공:', mp4Url)
        } catch (backendError) {
          logger.error('백엔드 이미지 변환 실패, 로컬 처리로 폴백:', backendError)
          // 폴백: 기존 로컬 API 사용
          mp4Url = await convertImageToMp4(imageFile, 5.0, '', subtitleSettings, videoSettings, titleSettings)
        }
      } else {
        // 프로젝트 ID가 없으면 기존 로컬 API 사용
        logger.debug('📻 기본 이미지 변환 API 사용 (로컬)')
        mp4Url = await convertImageToMp4(imageFile, 5.0, '', subtitleSettings, videoSettings, titleSettings)
      }
      
      const currentSection = getSectionById(sectionId)
      
      logger.debug('📺 전송할 비디오 설정:', videoSettings)
      logger.debug('🎨 변환된 MP4 URL (blob):', mp4Url)
      
      // 프로젝트 ID가 있으면 생성된 비디오를 Supabase에 업로드
      let mp4StorageUrl = null
      if (projectId && projectId !== 'new' && mp4Url) {
        try {
          logger.debug('🎯 생성된 비디오를 Supabase에 업로드 중...')
          const { uploadProjectMedia } = await import('../services/api')
          
          // blob URL을 파일로 변환
          const response = await fetch(mp4Url)
          const blob = await response.blob()
          const mp4File = new File([blob], `video_${sectionId}.mp4`, { type: 'video/mp4' })
          
          // Supabase에 업로드
          mp4StorageUrl = await uploadProjectMedia(projectId, sectionId, mp4File)
          logger.debug('✅ 비디오 Supabase 업로드 성공:', mp4StorageUrl)
        } catch (error) {
          logger.error('비디오 Supabase 업로드 실패:', error)
          // 실패해도 로컬 blob URL은 사용 가능
        }
      }
      
      // 해당 섹션에 MP4 파일 정보 업데이트
      updateSectionFiles(sectionId, {
        image: imageFile,
        imageUrl: currentSection?.imageUrl,  // 기존 imageUrl 유지
        video: currentSection?.videoFile,
        audio: currentSection?.audioFile,
        mp4: mp4Url,  // 로컬 blob URL (미리보기용)
        videoUrl: mp4StorageUrl,  // Supabase 비디오 URL (백엔드용)
        hasAudioInVideo: currentSection?.hasAudioInVideo || false
      })

      logger.debug('✅ 자막 포함 MP4 변환 완료')
      return mp4Url

    } catch (error) {
      logger.error('❌ MP4 변환 오류:', error)
      logger.error('오류 상세:', {
        message: error.message,
        stack: error.stack
      })
      alert(`이미지를 MP4로 변환하는 중 오류가 발생했습니다.\n\n${error.message}`)
      return null
    } finally {
      setIsConvertingMp4(prev => ({ ...prev, [sectionId]: false }))
    }
  }

  // 전체 TTS 생성 (병렬 처리)
  const handleGenerateAllTTS = async (sections, selectedVoice, updateSectionFiles) => {
    if (sections.length === 0) {
      alert('먼저 대본을 구간 분리해주세요.')
      return
    }
    
    // 프로젝트 ID가 있고 백엔드 API를 사용할 수 있는 경우 - 구간별 병렬 처리
    if (projectId && projectId !== 'new') {
      try {
        logger.debug('🎯 백엔드 구간별 TTS 생성 실행')
        setIsGeneratingAllTTS(true)
        
        // 각 섹션을 개별적으로 병렬 생성 (실시간 반영 가능)
        const sectionsNeedingTTS = sections.filter(section => section.text && section.text.trim() && !section.audioFile)
        
        if (sectionsNeedingTTS.length === 0) {
          alert('이미 모든 섹션에 TTS가 생성되어 있습니다.')
          setIsGeneratingAllTTS(false)
          return
        }
        
        logger.debug(`🎤 ${sectionsNeedingTTS.length}개 섹션을 구간별로 병렬 TTS 생성 시작`)
        
        // 모든 섹션을 동시에 진행 상태로 설정
        sectionsNeedingTTS.forEach(section => {
          setIsGeneratingTTS(prev => ({ ...prev, [section.id]: true }))
        })
        
        // 구간별 TTS 생성을 병렬로 실행
        const ttsPromises = sectionsNeedingTTS.map(async (section, index) => {
          try {
            logger.debug(`🎤 구간 ${index + 1} (ID: ${section.id}) TTS 생성 중: "${section.text.substring(0, 30)}..."`)
            
            // 개별 구간 TTS 생성 (백엔드 API)
            const audioUrl = await generateProjectTTS(
              projectId, 
              section.id, 
              section.text, 
              { voice: selectedVoice, speed: 1.0 }
            )
            
            logger.debug(`🔗 구간 ${index + 1} (ID: ${section.id}) 오디오 URL: ${audioUrl}`)
            
            // 실시간으로 미리보기 업데이트
            updateSectionFiles(section.id, {
              image: section.imageFile,
              video: section.videoFile,
              url: section.urlMedia,
              audio: audioUrl,
              mp4: section.mp4File,
              hasAudioInVideo: section.hasAudioInVideo || false,
              imageUrl: section.imageUrl,
              videoUrl: section.videoUrl
            })

            logger.debug(`✅ 구간 ${index + 1} TTS 생성 완료 - 실시간 반영됨`)
            return { success: true, sectionId: section.id, index: index + 1 }
          } catch (error) {
            logger.error(`❌ 구간 ${index + 1} (ID: ${section.id}) TTS 생성 실패:`, error)
            return { success: false, sectionId: section.id, index: index + 1, error }
          } finally {
            // 개별 섹션 진행 상태 해제
            setIsGeneratingTTS(prev => ({ ...prev, [section.id]: false }))
          }
        })
        
        // 모든 TTS 생성 작업 병렬 실행
        const results = await Promise.allSettled(ttsPromises)
        const successful = results.filter(result => 
          result.status === 'fulfilled' && result.value.success
        ).length
        const failed = results.filter(result => 
          result.status === 'rejected' || (result.status === 'fulfilled' && !result.value.success)
        ).length

        logger.debug(`✅ 구간별 TTS 생성 완료: 성공 ${successful}개, 실패 ${failed}개`)
        
        setIsGeneratingAllTTS(false)
        return
      } catch (error) {
        logger.error('백엔드 구간별 TTS 생성 실패, 로컬 처리로 폴백:', error)
        setIsGeneratingAllTTS(false)
        
        // 오류 발생 시 모든 진행 상태 해제
        sections.forEach(section => {
          setIsGeneratingTTS(prev => ({ ...prev, [section.id]: false }))
        })
        // 폴백: 아래 로컬 처리 계속 진행
      }
    }

    // TTS가 없는 섹션들만 필터링
    const sectionsNeedingTTS = sections.filter(section => !section.audioFile)
    
    if (sectionsNeedingTTS.length === 0) {
      alert('모든 섹션에 이미 음성이 생성되어 있습니다.')
      return
    }

    setIsGeneratingAllTTS(true)
    logger.debug(`🚀 전체 TTS 생성 시작: ${sectionsNeedingTTS.length}개 섹션을 병렬로 처리합니다`)
    
    // 모든 섹션을 동시에 진행 상태로 설정
    sectionsNeedingTTS.forEach(section => {
      setIsGeneratingTTS(prev => ({ ...prev, [section.id]: true }))
    })
    
    try {
      // 병렬로 TTS 생성
      const ttsPromises = sectionsNeedingTTS.map(async (section, index) => {
        try {
          logger.debug(`🎤 구간 ${index + 1} (ID: ${section.id}) TTS 생성 중: "${section.text.substring(0, 30)}..."`)
          const audioUrl = await generateTTS(section.text, selectedVoice)
          logger.debug(`🔗 구간 ${index + 1} (ID: ${section.id}) 오디오 URL: ${audioUrl}`)
          
          updateSectionFiles(section.id, {
            image: section.imageFile,
            video: section.videoFile,
            url: section.urlMedia,
            audio: audioUrl
          })

          logger.debug(`✅ 구간 ${index + 1} TTS 생성 완료`)
          return { success: true, sectionId: section.id, index: index + 1 }

        } catch (error) {
          logger.error(`❌ 구간 ${index + 1} TTS 생성 오류:`, error)
          return { success: false, sectionId: section.id, index: index + 1, error }
        } finally {
          setIsGeneratingTTS(prev => ({ ...prev, [section.id]: false }))
        }
      })

      // 모든 TTS 생성 작업 병렬 실행
      const results = await Promise.allSettled(ttsPromises)
      const successful = results.filter(result => 
        result.status === 'fulfilled' && result.value.success
      ).length
      const failed = results.filter(result => 
        result.status === 'rejected' || (result.status === 'fulfilled' && !result.value.success)
      ).length

      logger.debug(`✅ 전체 TTS 생성 완료: 성공 ${successful}개, 실패 ${failed}개`)
      
      if (failed > 0) {
        alert(`전체 음성 생성이 완료되었습니다!\n성공: ${successful}개, 실패: ${failed}개`)
      } else {
        alert(`전체 음성 생성이 완료되었습니다! (${successful}개 섹션)`)
      }

    } catch (error) {
      logger.error('전체 TTS 생성 오류:', error)
      alert('전체 음성 생성 중 오류가 발생했습니다.')
      
      // 오류 발생 시 모든 진행 상태 해제
      sectionsNeedingTTS.forEach(section => {
        setIsGeneratingTTS(prev => ({ ...prev, [section.id]: false }))
      })
    } finally {
      setIsGeneratingAllTTS(false)
    }
  }

  // 전체 영상 생성 (병렬 처리)
  const handleGenerateAllVideos = async (sections, selectedVoice, subtitleSettings, videoSettings, updateSectionFiles, getSectionById) => {
    if (sections.length === 0) {
      alert('먼저 대본을 구간 분리해주세요.')
      return
    }

    // 이미지는 있지만 MP4가 없는 섹션들만 필터링
    const sectionsNeedingVideos = sections.filter(section => section.imageFile && !section.mp4File)
    
    if (sectionsNeedingVideos.length === 0) {
      alert('모든 섹션에 이미 영상이 생성되어 있습니다.')
      return
    }

    setIsGeneratingAllVideos(true)
    logger.debug(`🚀 전체 영상 생성 시작: ${sectionsNeedingVideos.length}개 섹션을 병렬로 처리합니다`)
    
    // 모든 섹션을 동시에 진행 상태로 설정
    sectionsNeedingVideos.forEach(section => {
      setIsConvertingMp4(prev => ({ ...prev, [section.id]: true }))
    })
    
    try {
      // 병렬로 영상 생성
      const videoPromises = sectionsNeedingVideos.map(async (section, index) => {
        try {
          logger.debug(`🎬 구간 ${index + 1} 영상 생성 중: "${section.text.substring(0, 30)}..."`)
          
          let mp4Url
          if (section.audioFile) {
            // TTS가 있으면 TTS 포함 영상 생성
            logger.debug(`🎵 구간 ${index + 1}: TTS 포함 영상 생성`)
            mp4Url = await handleGenerateVideoWithTTS(
              section.id, 
              section.imageFile, 
              section.text, 
              selectedVoice, 
              subtitleSettings, 
              updateSectionFiles, 
              getSectionById, 
              null  // videoSettings 제거 - Canvas가 실시간 조정
            )
          } else {
            // TTS가 없으면 자막만 포함된 영상 생성
            logger.debug(`📝 구간 ${index + 1}: 자막 영상 생성`)
            mp4Url = await handleConvertImageToMp4(
              section.id, 
              section.imageFile, 
              getSectionById, 
              updateSectionFiles, 
              subtitleSettings, 
              null, 
              null  // videoSettings 제거 - Canvas가 실시간 조정
            )
          }

          logger.debug(`✅ 구간 ${index + 1} 영상 생성 완료`)
          return { success: true, sectionId: section.id, index: index + 1 }

        } catch (error) {
          logger.error(`❌ 구간 ${index + 1} 영상 생성 오류:`, error)
          return { success: false, sectionId: section.id, index: index + 1, error }
        } finally {
          setIsConvertingMp4(prev => ({ ...prev, [section.id]: false }))
        }
      })

      // 모든 영상 생성 작업 병렬 실행
      const results = await Promise.allSettled(videoPromises)
      const successful = results.filter(result => 
        result.status === 'fulfilled' && result.value.success
      ).length
      const failed = results.filter(result => 
        result.status === 'rejected' || (result.status === 'fulfilled' && !result.value.success)
      ).length

      logger.debug(`✅ 전체 영상 생성 완료: 성공 ${successful}개, 실패 ${failed}개`)
      
      // 성공적으로 생성된 영상들을 순서대로 이어붙이기
      if (successful > 0) {
        logger.debug('🔗 생성된 영상들을 순서대로 이어붙이는 중...')
        try {
          // 섹션 순서대로 MP4 URL들 수집
          const allSections = sections.sort((a, b) => a.id - b.id) // ID 순서로 정렬
          const mp4Urls = allSections
            .filter(section => section.mp4File) // MP4가 있는 섹션만
            .map(section => section.mp4File) // MP4 URL 추출

          if (mp4Urls.length > 1) {
            logger.debug(`🎬 ${mp4Urls.length}개 영상을 이어붙이는 중...`)
            const finalVideoUrl = await concatenateVideos(mp4Urls)
            
            // 이어붙인 영상을 다운로드 링크로 제공
            const link = document.createElement('a')
            link.href = finalVideoUrl
            link.download = `전체영상_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.mp4`
            link.click()
            
            logger.debug('🎉 전체 영상 이어붙이기 완료!')
            alert(`전체 영상 생성 및 이어붙이기가 완료되었습니다!\n성공: ${successful}개, 실패: ${failed}개\n\n이어붙인 영상이 다운로드됩니다.`)
          } else {
            alert(`전체 영상 생성이 완료되었습니다!\n성공: ${successful}개, 실패: ${failed}개`)
          }
        } catch (concatenateError) {
          logger.error('❌ 영상 이어붙이기 실패:', concatenateError)
          alert(`전체 영상 생성이 완료되었지만 이어붙이기에 실패했습니다.\n성공: ${successful}개, 실패: ${failed}개\n\n개별 영상은 각 구간에서 확인할 수 있습니다.`)
        }
      } else if (failed > 0) {
        alert(`전체 영상 생성이 완료되었습니다!\n성공: ${successful}개, 실패: ${failed}개`)
      }

    } catch (error) {
      logger.error('전체 영상 생성 오류:', error)
      alert('전체 영상 생성 중 오류가 발생했습니다.')
      
      // 오류 발생 시 모든 진행 상태 해제
      sectionsNeedingVideos.forEach(section => {
        setIsConvertingMp4(prev => ({ ...prev, [section.id]: false }))
      })
    } finally {
      setIsGeneratingAllVideos(false)
    }
  }

  // 섹션을 MP4로 준비하는 함수 (SubtitleDownloadButton 로직 재사용)
  const prepareVideoForMerge = async (section, settings = {}) => {
    const { subtitleSettings, titleSettings, videoSettings } = settings
    
    const formData = new FormData()
    
    // 파일 처리 (SubtitleDownloadButton과 동일한 로직)
    if (section.imageFile) {
      // 이미지 또는 GIF 파일
      logger.debug(`🖼️ ${section.index}번 구간: 이미지/GIF → MP4 변환`)
      formData.append('file', section.imageFile, section.imageFile.name)
      formData.append('is_original_image', 'true')
      const videoDuration = section.audioDuration || 5.0
      formData.append('video_duration', videoDuration.toString())
    } else if (section.videoFile) {
      // 원본 비디오 파일
      logger.debug(`🎥 ${section.index}번 구간: 비디오 처리`)
      formData.append('file', section.videoFile, section.videoFile.name)
      formData.append('is_original_image', 'false')
    } else if (section.urlMedia) {
      // URL로 업로드된 미디어 파일 (GIF, 이미지, 비디오)
      logger.debug(`🌐 ${section.index}번 구간: URL 미디어 처리`)
      try {
        const response = await fetch(section.urlMedia)
        const blob = await response.blob()
        const filename = section.urlMedia.split('/').pop() || 'media'
        formData.append('file', blob, filename)
        
        // URL에서 파일 타입 추정
        const isVideo = /\.(mp4|webm|mov|avi|mkv)$/i.test(section.urlMedia)
        formData.append('is_original_image', isVideo ? 'false' : 'true')
        
        if (!isVideo) {
          const videoDuration = section.audioDuration || 5.0
          formData.append('video_duration', videoDuration.toString())
        }
      } catch (error) {
        logger.error(`❌ ${section.index}번 구간: URL 미디어 로드 실패:`, error)
        throw new Error(`${section.index}번 구간: URL 미디어 로드 실패`)
      }
    } else if (section.mp4File) {
      // 이미 생성된 MP4
      logger.debug(`📹 ${section.index}번 구간: 기존 MP4 사용`)
      const response = await fetch(section.mp4File)
      const blob = await response.blob()
      formData.append('file', blob, 'video.mp4')
      formData.append('is_original_image', 'false')
    } else {
      throw new Error(`${section.index}번 구간: 미디어 파일 없음 (imageFile, videoFile, urlMedia 모두 없음)`)
    }
    
    // 자막 텍스트
    formData.append('subtitle_text', section.text || '')
    
    // 음성 파일 (있는 경우)
    if (section.audioFile) {
      logger.debug(`🎵 ${section.index}번 구간: 음성 추가`)
      const audioResponse = await fetch(section.audioFile)
      const audioBlob = await audioResponse.blob()
      formData.append('audio_file', audioBlob, `audio_${section.id}.mp3`)
    }
    
    // 설정 추가
    formData.append('subtitle_settings', JSON.stringify(subtitleSettings || {}))
    if (titleSettings && titleSettings.enabled && titleSettings.text) {
      formData.append('title_settings', JSON.stringify(titleSettings))
    }
    if (videoSettings) {
      formData.append('video_settings', JSON.stringify(videoSettings))
    }
    
    // 서버로 요청
    const response = await fetch(`${API_BASE}/api/video/add-subtitle`, {
      method: 'POST',
      body: formData
    })
    
    if (!response.ok) {
      throw new Error(`${section.index}번 구간 변환 실패`)
    }
    
    const mp4Blob = await response.blob()
    return URL.createObjectURL(mp4Blob)
  }

  // 선택된 영상들 합치기 (현재 설정으로 자동 재생성)
  const handleMergeSelectedVideos = async (sectionsToMerge, currentSettings = {}) => {
    setIsMergingVideos(true)
    setMergeProgress(0)
    setMergeStatus('준비 중...')
    
    try {
      logger.debug(`🎬 영상 합치기 시작: ${sectionsToMerge.length}개 구간`)
      logger.debug('받은 섹션들:', sectionsToMerge)
      logger.debug('현재 설정:', currentSettings)
      
      // 현재 설정 추출
      const { subtitleSettings, titleSettings, videoSettings } = currentSettings
      
      // 프로젝트 ID가 있으면 백엔드 API 사용
      if (projectId && projectId !== 'new') {
        logger.debug('🎯 Supabase 기반 영상 합치기 실행')
        setMergeStatus('Supabase 기반 영상 합치기 중...')
        
        try {
          // 백엔드 API 호출 - 섹션 전체 데이터 전달
          const mergedVideoUrl = await concatenateProjectVideos(
            projectId,
            sectionsToMerge,  // 섹션 ID만이 아닌 전체 데이터 전달
            {
              subtitleSettings,
              titleSettings,
              videoSettings
            }
          )
          
          setMergeProgress(90)
          logger.debug('✅ 백엔드 영상 합치기 완료:', mergedVideoUrl)
          
          // URL이면 blob으로 변환
          let mergedVideoBlob
          if (typeof mergedVideoUrl === 'string') {
            const response = await fetch(mergedVideoUrl)
            mergedVideoBlob = await response.blob()
          } else {
            mergedVideoBlob = mergedVideoUrl // 이미 blob인 경우
          }
          
          // 다운로드 진행
          const now = new Date()
          const year = now.getFullYear()
          const month = String(now.getMonth() + 1).padStart(2, '0')
          const day = String(now.getDate()).padStart(2, '0')
          const time = now.toTimeString().slice(0, 8).replace(/:/g, '-')
          
          const sectionRange = sectionsToMerge.length === sectionsToMerge.length 
            ? '전체' 
            : `${sectionsToMerge[0].index}-${sectionsToMerge[sectionsToMerge.length-1].index}번`
          
          const fileName = `merged_videos_${year}${month}${day}_${sectionRange}_${time}.mp4`
          
          const url = URL.createObjectURL(mergedVideoBlob)
          const link = document.createElement('a')
          link.href = url
          link.download = fileName
          link.click()
          
          setTimeout(() => URL.revokeObjectURL(url), 1000)
          
          setMergeProgress(100)
          setMergeStatus('완료!')
          logger.debug('🎉 영상 다운로드 완료!')
          
          return
        } catch (backendError) {
          logger.error('❌ Supabase 기반 영상 합치기 실패:', backendError)
          logger.error(`영상 합치기 실패: ${backendError.message} - Supabase 기반 API를 확인해주세요.`)
          return  // 로컬 폴백 하지 않고 종료
        }
      }
      
      // 로컬 처리 (프로젝트 ID가 없는 경우에만)
      logger.debug('⚠️ 프로젝트가 저장되지 않음. 로컬 영상 합치기 사용')
      setMergeStatus('영상 준비 중...')
      
      const preparedVideos = []
      const totalSections = sectionsToMerge.length
      let processedSections = 0
      
      for (const section of sectionsToMerge) {
        try {
          logger.debug(`📦 ${section.index}번 구간 처리 중...`)
          setMergeStatus(`${section.index}번 구간 처리 중...`)
          
          let mp4File = null
          
          // 현재 설정으로 새로운 MP4 생성 (기존 MP4가 있어도 재생성)
          logger.debug(`🔄 ${section.index}번 구간: 현재 설정으로 MP4 생성 중...`)
          
          // prepareVideoForMerge 함수를 사용하여 모든 설정이 적용된 MP4 생성
          mp4File = await prepareVideoForMerge(section, {
            subtitleSettings,
            titleSettings,
            videoSettings
          })
          
          if (mp4File) {
            preparedVideos.push({
              ...section,
              mp4File: mp4File
            })
            logger.debug(`✅ ${section.index}번 구간 준비 완료 (설정 적용됨)`)
          } else {
            logger.warn(`⚠️ ${section.index}번 구간: MP4 생성 실패`)
          }
          
          // 진행률 업데이트 (준비 단계는 전체의 60%)
          processedSections++
          const prepareProgress = Math.round((processedSections / totalSections) * 60)
          setMergeProgress(prepareProgress)
          
        } catch (error) {
          logger.error(`❌ ${section.index}번 구간 처리 실패:`, error)
        }
      }
      
      const validSections = preparedVideos.filter(section => section.mp4File)
      logger.debug(`📹 합칠 수 있는 영상: ${validSections.length}/${sectionsToMerge.length}개`)
      
      if (validSections.length === 0) {
        logger.error('합칠 수 있는 영상을 생성하지 못했습니다. 미디어 파일(이미지, GIF, 비디오)을 확인해 주세요.')
        return
      }
      
      if (validSections.length === 1) {
        logger.warn('합칠 영상이 1개뿐입니다. 2개 이상의 영상이 필요합니다.')
        return
      }
      
      // 영상 파일 경로 배열 생성
      const videoPaths = validSections.map(section => section.mp4File)
      logger.debug('📁 영상 파일 경로들:', videoPaths)
      
      // 영상 이어붙이기 API 호출 (60% ~ 90%)
      setMergeStatus('영상 합치는 중...')
      setMergeProgress(70)
      logger.debug('🔄 API 호출 시작...')
      
      const mergedVideoBlob = await concatenateVideos(videoPaths)
      
      setMergeProgress(90)
      logger.debug('✅ API 응답 받음:', mergedVideoBlob)
      
      // 다운로드 링크 생성 - 날짜별 폴더 구조를 파일명에 포함
      const now = new Date()
      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      const day = String(now.getDate()).padStart(2, '0')
      const time = now.toTimeString().slice(0, 8).replace(/:/g, '-')
      
      // 선택된 구간 정보를 파일명에 포함
      const sectionRange = validSections.length === sectionsToMerge.length 
        ? '전체' 
        : `${validSections[0].index}-${validSections[validSections.length-1].index}번`
      
      // 폴더 구조를 파일명에 표시: merged_videos/2024-01-22/전체_15-30-45.mp4
      const fileName = `merged_videos_${year}${month}${day}_${sectionRange}_${time}.mp4`
      
      // 다운로드 준비 (90% ~ 100%)
      setMergeStatus('다운로드 준비 중...')
      setMergeProgress(95)
      
      const url = URL.createObjectURL(mergedVideoBlob)
      const link = document.createElement('a')
      link.href = url
      link.download = fileName
      link.click()
      
      setMergeProgress(100)
      setMergeStatus('완료!')
      
      logger.debug('✅ 영상 합치기 완료!')
      
      // 잠시 후 완료 메시지 표시
      logger.info(`✅ ${validSections.length}개 구간의 영상이 성공적으로 합쳐졌습니다!`)
      
    } catch (error) {
      logger.error('❌ 영상 합치기 실패:', error)
      logger.error('오류 상세:', error.message, error.stack)
      logger.error(`영상 합치기 중 오류가 발생했습니다: ${error.message || '알 수 없는 오류'}`)
    } finally {
      setIsMergingVideos(false)
      setMergeProgress(0)
      setMergeStatus('')
    }
  }

  return {
    isGeneratingTTS,
    isConvertingMp4,
    isGeneratingAllTTS,
    isGeneratingAllVideos,
    isMergingVideos,
    mergeProgress,
    mergeStatus,
    handleGenerateTTS,
    handleGenerateVideoWithTTS,
    handleConvertImageToMp4,
    handleAddSubtitleToVideo,
    handleGenerateAllTTS,
    handleGenerateAllVideos,
    handleMergeSelectedVideos
  }
}

export default useVideoGeneration